import React from "react";
import { motion } from "framer-motion";
import { AiOutlineExport } from "react-icons/ai";
import { Link } from "react-router-dom";

import Worklinq from "../assets/img/worklinq.png";
import SB from "../assets/img/securitybridge-website.png";
import Logpoint from "../assets/img/logpoint-website-scaled.webp";

const FEATURED = [
  {
    title: "Worklinq",
    description:
      "Full technical implementation of a WordPress site for a staffing platform — CRM integration, lead capture automation, and conversion tracking.",
    imgSrc: Worklinq,
    tech: ["WordPress", "PHP", "Salesforce", "GA4"],
    bgColor: "from-emerald-500 via-teal-500 to-cyan-500",
    url: "https://www.worklinq.com/",
  },
  {
    title: "SecurityBridge",
    description:
      "Complete rebrand and website overhaul with CRM integration, server optimization, advanced tracking, and ongoing SEO consulting.",
    imgSrc: SB,
    tech: ["WordPress", "PHP", "Salesforce", "Cloudflare", "GA4"],
    bgColor: "from-violet-500 to-indigo-500",
    url: "https://securitybridge.com/",
  },
  {
    title: "Logpoint",
    description:
      "End-to-end rebranding project: website development, CRM integration, server maintenance, analytics setup, and regular performance reporting.",
    imgSrc: Logpoint,
    tech: ["WordPress", "PHP", "Salesforce", "Cloudflare", "GA4"],
    bgColor: "from-violet-500 to-indigo-500",
    url: "https://logpoint.com/",
  },
];

const FeaturedWork = () => {
  return (
    <section className="bg-neutral-950 py-24 md:py-32">
      <div className="mx-auto w-full max-w-7xl px-5 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-12 flex flex-col gap-4 md:flex-row md:items-end md:justify-between"
        >
          <div>
            <h2 className="text-4xl font-black text-white md:text-5xl">
              Recent Work
            </h2>
            <p className="mt-3 text-lg text-neutral-400">
              A few of my latest projects — each one built to last.
            </p>
          </div>
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-2 font-semibold text-indigo-400 transition-colors hover:text-indigo-300 group"
          >
            View all projects
            <AiOutlineExport className="transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {FEATURED.map((item, index) => (
            <FeaturedCard key={index} {...item} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

const FeaturedCard = ({ title, description, imgSrc, tech, bgColor, url, index }) => {
  return (
    <motion.a
      href={url}
      target="_blank"
      rel="noreferrer"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -4 }}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/5 bg-neutral-900 transition-all duration-300 hover:border-white/10"
    >
      {/* Image */}
      <div className="relative h-52 overflow-hidden">
        <div className={`absolute inset-0 bg-gradient-to-br ${bgColor} opacity-20`} />
        <img
          src={imgSrc}
          alt={`${title} project screenshot`}
          className="h-full w-full object-cover object-top opacity-70 transition-all duration-500 group-hover:scale-105 group-hover:opacity-90"
        />
        <div className="absolute right-3 top-3 flex items-center gap-1.5 rounded-full bg-black/50 px-3 py-1.5 text-sm text-white/60 backdrop-blur-sm transition-colors group-hover:text-white">
          <AiOutlineExport />
          <span>Visit</span>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col gap-3 p-6">
        <h3 className="text-xl font-bold text-white">{title}</h3>
        <p className="text-sm leading-relaxed text-neutral-400">{description}</p>
        <div className="mt-1 flex flex-wrap gap-2">
          {tech.map((t) => (
            <span
              key={t}
              className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs text-neutral-300"
            >
              {t}
            </span>
          ))}
        </div>
      </div>
    </motion.a>
  );
};

export default FeaturedWork;
